import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import baseapi from "../../services/baseapi";
import { showError, showSuccess } from "../../utils/toast";
import {
  X,
  Check,
  ChevronDown,
  Loader2,
  Play
} from "lucide-react";

interface ScanOptionsModalProps {
  open: boolean;
  onClose: () => void;
  assetId: number | null;
  assetName: string;
}

const scanOptions = [
  { value: "website_scanner", label: "Website Scan" },
  { value: "port_scanner", label: "Port Scan" },
  { value: "nmap_scanner", label: "Nmap Scan" },
  { value: "ssl_checker", label: "SSL Checker" },
  { value: "subdomain_finder", label: "Subdomain Scan" },
  { value: "server_fingerprint", label: "Server Scan" },
  { value: "custom_scan", label: "Ovul Custom Scan" }
];

export default function ScanOptionsModal({
  open,
  onClose,
  assetId,
  assetName,
}: ScanOptionsModalProps) {
  const [loading, setLoading] = useState(false);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  // Reset selected modules when modal opens
  useEffect(() => {
    if (open) {
      setSelectedModules([]);
      setDropdownOpen(false);
      setLoading(false);
    }
  }, [open]);

  if (!open || !assetId) return null;

  const handleToggleModule = (val: string) => {
    setSelectedModules((prev) =>
      prev.includes(val) ? prev.filter((m) => m !== val) : [...prev, val]
    );
  };

  const handleSelectAll = () => {
    setSelectedModules(scanOptions.map((opt) => opt.value));
  };

  const handleClearAll = () => {
    setSelectedModules([]);
  };

  const getDropdownButtonText = () => {
    if (selectedModules.length === 0) {
      return "Select scan modules...";
    }
    if (selectedModules.length === scanOptions.length) {
      return "All Modules Selected";
    }
    return `${selectedModules.length} module${selectedModules.length > 1 ? "s" : ""} selected`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedModules.length === 0) {
      showError("Please select at least one scan module.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        asset_id: assetId,
        modules: selectedModules,
      };

      const response = await baseapi.post("/external_scan/", payload);

      if (response.data && response.data.status) {
        showSuccess("Scan task triggered successfully");
        onClose();
      } else {
        showError(response.data.message || "Failed to trigger scan");
      }
    } catch (error: any) {
      showError(error.message || "An error occurred while triggering scan");
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
        onClick={onClose}
      />
      <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4">
        <div className="w-full max-w-md bg-gray-900/95 backdrop-blur-xl border border-orange-400/20 shadow-2xl rounded-xl text-white relative">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-400/40 to-transparent" />

          {/* Header */}
          <div className="sticky top-0 bg-gray-900/95 backdrop-blur-xl border-b border-gray-800 px-6 py-4 z-10 rounded-t-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Play className="h-5 w-5 text-orange-400" />
                <h2 className="text-lg font-bold text-white truncate max-w-[280px]" title={assetName}>
                  Scan: {assetName}
                </h2>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors p-1 rounded-full hover:bg-gray-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="space-y-2">
              <label className="text-xs font-semibold text-gray-300 uppercase tracking-wider block">
                Select Scan Modules <span className="text-red-500">*</span>
              </label>

              {/* Custom Multi-select Dropdown Container */}
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-sm text-left text-white flex items-center justify-between hover:bg-gray-700/80 focus:outline-none focus:border-orange-500   transition-all"
                >
                  <span className="truncate">{getDropdownButtonText()}</span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-2xl z-50 overflow-hidden flex flex-col max-h-64">
                    {/* Header Controls (Select All / Clear All) */}
                    <div className="flex items-center justify-between px-3 py-2 border-b border-gray-800 bg-gray-850">
                      <button
                        type="button"
                        onClick={handleSelectAll}
                        className="text-[10px] font-bold text-orange-400 hover:text-orange-300 uppercase tracking-widest transition-colors"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={handleClearAll}
                        className="text-[10px] font-bold text-gray-400 hover:text-gray-300 uppercase tracking-widest transition-colors"
                      >
                        Clear All
                      </button>
                    </div>

                    {/* Checkboxes List */}
                    <div className="overflow-y-auto py-1">
                      {scanOptions.map((opt) => {
                        const isChecked = selectedModules.includes(opt.value);
                        return (
                          <label
                            key={opt.value}
                            className="flex items-center justify-between px-4 py-2.5 text-xs text-gray-300 hover:bg-gray-800 cursor-pointer select-none transition-colors"
                          >
                            <span>{opt.label}</span>
                            <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${isChecked ? "bg-orange-600 border-orange-600" : "border-gray-600 bg-gray-800"
                              }`}>
                              {isChecked && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <input
                              type="checkbox"
                              className="hidden"
                              checked={isChecked}
                              onChange={() => handleToggleModule(opt.value)}
                            />
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Badges of selected modules */}
              {selectedModules.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-2 max-h-24 overflow-y-auto">
                  {scanOptions
                    .filter((opt) => selectedModules.includes(opt.value))
                    .map((opt) => (
                      <span
                        key={opt.value}
                        className="inline-flex items-center gap-1 bg-orange-600/20 border border-orange-500/30 text-orange-400 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full select-none"
                      >
                        {opt.label}
                        <button
                          type="button"
                          onClick={() => handleToggleModule(opt.value)}
                          className="hover:text-red-400 transition-colors"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </span>
                    ))}
                </div>
              )}
            </div>

            {/* Footer Buttons */}
            <div className="flex justify-end gap-2 pt-4 border-t border-gray-850">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 hover:border-gray-500 disabled:opacity-50 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
                Cancel
              </button>

              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-1.5 px-4 py-1.5 text-xs bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-lg font-bold hover:from-orange-700 hover:to-orange-800 disabled:opacity-50 min-w-[120px] justify-center transition-all shadow-lg shadow-orange-900/20"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <Play className="w-3.5 h-3.5" />
                    Start Scan
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body
  );
}
